import React, { FC, Fragment } from 'react';
import { Box, Button, Card, Checkbox, Chip, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Restoration, TeethSelectionType } from 'models/order';
import { formatTeethList, formatTeethRange } from 'utils/teeth';
import { useSubcontractOrder } from 'pages/order-flow/subcontract-order/subcontract-order-context';
import {
    displayProperties,
    formatPropertyValue,
    NUMBER_OF_UNITS_KEY,
    propertyByKey,
} from 'components/order-details/main-order-view/restorations-section/restoration-format';
import InfoText from 'components/shared/info-text.component';

type RestorationsStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};

const teethSummaryOf = (restoration: Restoration, translateJaw: (jaw: string) => string): string => {
    const { teeth } = restoration;
    switch (teeth.type) {
        case TeethSelectionType.TEETH:
            return formatTeethList(teeth.selectedTeeth ?? []);
        case TeethSelectionType.RANGE:
            return teeth.selectedRange ? formatTeethRange(teeth.selectedRange.start, teeth.selectedRange.end) : '';
        case TeethSelectionType.JAW:
            return teeth.selectedJaw ? translateJaw(teeth.selectedJaw) : '';
        default:
            return '';
    }
};

/**
 * The restoration's properties as one line — unit counts first, then the rest, formatted as
 * on order details. Properties travel to the sub-order; actions and materials do not, so
 * they are not shown here.
 */
const propertiesSummaryOf = (restoration: Restoration, translate: TFunction): string => {
    const units = (propertyByKey(restoration, NUMBER_OF_UNITS_KEY)?.units ?? []).map(
        (unit) => `${unit.label}: ${unit.value}`,
    );
    const properties = displayProperties(restoration)
        .map((property) => ({
            label: translate(`orders:${property.key}`, { defaultValue: property.key }),
            value: formatPropertyValue(property, translate),
        }))
        .filter(({ value }) => value)
        .map(({ label, value }) => `${label}: ${value}`);
    return [...units, ...properties].join(' · ');
};

/**
 * DD-3484: select which parts of the production the sub-order covers — a checkbox per
 * parent restoration. The rows travel as their shape only (type, teeth, properties);
 * the parent's actions, materials and prices never do.
 */
const RestorationsStep: FC<RestorationsStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate } = useTranslation(['orders', 'common', 'restoration-types', 'construction-props']);
    const { parentOrder, subcontract, updateSelection } = useSubcontractOrder();
    const restorations = parentOrder.restorations ?? [];
    const selectedIds = subcontract.selection.restorationIds;

    const toggleRestoration = (restorationId: string) => {
        updateSelection({
            restorationIds: selectedIds.includes(restorationId)
                ? selectedIds.filter((selectedId) => selectedId !== restorationId)
                : [...selectedIds, restorationId],
        });
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Typography variant="h1">{translate('orders:CONSTRUCTIONS')}</Typography>
                <InfoText text={translate('orders:SUBCONTRACT_SELECT_RESTORATIONS_INFO')} />
            </Box>
            {restorations.map((restoration) => {
                const restorationId = String(restoration._id);
                const teethSummary = teethSummaryOf(restoration, (jaw) => translate(`orders:${jaw}`));
                const propertiesSummary = propertiesSummaryOf(restoration, translate);
                return (
                    <Card key={restorationId} variant="outlined" sx={{ mb: 1 }}>
                        <Box p={1.5} display="flex" alignItems="center">
                            <FormControlLabel
                                sx={{ flexGrow: 1 }}
                                control={
                                    <Checkbox
                                        checked={selectedIds.includes(restorationId)}
                                        onChange={() => toggleRestoration(restorationId)}
                                        data-test={`restorations-step.select-${restorationId}`}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="subtitle2">
                                            {translate(`restoration-types:${restoration.type}`, {
                                                defaultValue: restoration.type,
                                            })}
                                        </Typography>
                                        {teethSummary ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {teethSummary}
                                            </Typography>
                                        ) : undefined}
                                        {propertiesSummary ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                data-test={`restorations-step.properties-${restorationId}`}
                                            >
                                                {propertiesSummary}
                                            </Typography>
                                        ) : undefined}
                                    </Box>
                                }
                            />
                            {restoration.warranty ? (
                                <Chip size="small" label={translate('orders:WARRANTY')} />
                            ) : undefined}
                        </Box>
                    </Card>
                );
            })}
            <Box display="flex" justifyContent="space-between" mt={2}>
                <Button onClick={onBackClick} data-test="restorations-step.back-btn">
                    {translate('common:BACK')}
                </Button>
                <Button
                    variant="contained"
                    disabled={!selectedIds.length}
                    onClick={onNextClick}
                    data-test="restorations-step.next-btn"
                >
                    {translate('orders:NEXT_STEP')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default RestorationsStep;
